import { BaseScreenProps } from '@/features/quiz/types'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  BaseMedicareFormType,
  BaseMedicareFormValidator,
} from '@/shared/components/hi/MedicareFormValidator'
import BottomContinueButton from '@/features/quiz/bottom-continue-button'
import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import QuizTextInput from '@/features/quiz/inputs/quiz-text-input'
import {
  IHIFormType,
  IHIFormValidator,
} from '@/shared/components/hi/IHIFormValidator'
import { AnimatePresence, motion } from 'framer-motion'
import { MYGOV_LINK } from '@/lib/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUpRight,
  faCheck,
  faMinus,
  faPlus,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { DialogTitle } from '@headlessui/react'
import SlideOutMenu from '@/features/quiz/slide-out-menu'
import { Container } from '@/shared/components/Container'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { SCREEN_STEP_PARAM } from '@/features/quiz/constants'
import { HEALTH_IDENTIFIER_DEFERRED_KEY } from '@/features/quiz/quiz-screen'
import {
  GET_IHI_INTRO,
  GET_IHI_STEPS,
} from '@/shared/components/hi/get-ihi-steps'

type Props = BaseScreenProps

const identifierSteps = ['ihi'] as const

function startsOnIhi({ answers, screenId, identifier }: Props) {
  const answer = answers[screenId]
  if (answer?.type === 'health-identifier') {
    return answer.identifier.type === 'ihi'
  }
  return identifier?.type === 'ihi'
}

export default function MedicareCardScreen(props: Props) {
  // The container clears this when the screen changes and uses it for Back.
  const [step, setStep] = useQueryState(
    SCREEN_STEP_PARAM,
    parseAsStringLiteral(identifierSteps).withOptions({ history: 'push' }),
  )
  const [showInformation, setShowInformation] = useState(false)
  // Kept here so Back from the IHI step shows the box still ticked.
  const [noMedicareCard, setNoMedicareCard] = useState(false)

  const initialised = useRef(false)
  useEffect(() => {
    if (initialised.current) return
    initialised.current = true
    // A saved IHI opens on the IHI step, ready to continue.
    if (step === null && startsOnIhi(props)) {
      void setStep('ihi', { history: 'replace' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onIhiStep = step === 'ihi'
  const showIhiStep = useCallback(() => void setStep('ihi'), [setStep])
  const showInformationSheet = useCallback(() => setShowInformation(true), [])

  return (
    <>
      <div className="mb-16 w-full">
        <div className="mx-auto max-w-3xl">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={onIhiStep ? 'ihi' : 'medicare'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ ease: 'easeOut' }}
              className="space-y-5 sm:space-y-6"
            >
              <h1 className="text-xl font-medium leading-7 tracking-tight text-gray-900 sm:text-2xl sm:leading-8">
                {onIhiStep
                  ? 'Do you have an IHI number?'
                  : 'Please provide your Medicare card details.'}
              </h1>
              {onIhiStep ? (
                <IhiStep {...props} onShowInformation={showInformationSheet} />
              ) : (
                <MedicareStep
                  {...props}
                  noMedicareCard={noMedicareCard}
                  onNoMedicareCardChange={setNoMedicareCard}
                  onNoMedicareCard={showIhiStep}
                  onShowInformation={showInformationSheet}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
      <SlideOutMenu
        show={showInformation}
        onClose={() => setShowInformation(false)}
      >
        <Container>
          <div className="mb-2.5 flex items-center justify-end">
            <button
              type="button"
              aria-label="Close information"
              className="text-xl text-gray-900"
              onClick={() => setShowInformation(false)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <DialogTitle className="text-xl font-medium tracking-tight text-gray-900 sm:text-2xl">
            Why do we need your Medicare or IHI information?
          </DialogTitle>
          <div className="mt-4 space-y-2 text-sm tracking-tight text-gray-800">
            <p>
              For your safety and to comply with Australian healthcare
              regulations, we must verify your identity.
            </p>
            <p>
              Your <b>Medicare number</b> or{' '}
              <b>Individual Healthcare Identifier (IHI) number</b> is necessary
              for our practitioners to legally issue an electronic prescription
              in your name.
            </p>
          </div>
        </Container>
      </SlideOutMenu>
    </>
  )
}

function WhyRow({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-haspopup="dialog"
      className="flex w-full items-center justify-between px-5 py-4 text-left text-sm leading-5 text-gray-800 transition-colors hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-gray-900"
      onClick={onClick}
    >
      <span>Why do we need this</span>
      <FontAwesomeIcon icon={faPlus} className="shrink-0" />
    </button>
  )
}

type MedicareStepProps = Props & {
  noMedicareCard: boolean
  onNoMedicareCardChange: (checked: boolean) => void
  onNoMedicareCard: () => void
  onShowInformation: () => void
}

function MedicareStep({
  answers,
  screenId,
  onChange,
  identifier,
  noMedicareCard,
  onNoMedicareCardChange,
  onNoMedicareCard,
  onShowInformation,
}: MedicareStepProps) {
  const answer = answers[screenId]

  let defaultValues: object = { irn: 1 }
  if (
    answer &&
    answer.type === 'health-identifier' &&
    answer.identifier.type === 'med'
  ) {
    defaultValues = answer.identifier
  } else if (identifier && identifier.type === 'med') {
    defaultValues = identifier
  }

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onTouched',
    defaultValues: BaseMedicareFormValidator.cast(defaultValues),
    resolver: yupResolver(BaseMedicareFormValidator),
  })

  const { handleSubmit, formState } = methods
  const { isValid, isSubmitting } = formState

  async function onSubmit(values: BaseMedicareFormType) {
    await onChange(
      screenId,
      {
        id: screenId,
        type: 'health-identifier',
        identifier: {
          type: 'med',
          number: values.number,
          expiry: values.expiry,
          irn: 1,
        },
      },
      true,
    )
  }

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-6 pb-6 sm:pb-11"
        onSubmit={(event) => {
          if (!noMedicareCard) return handleSubmit(onSubmit)(event)
          // No card to save: Continue opens the IHI step.
          event.preventDefault()
          onNoMedicareCard()
        }}
      >
        <fieldset
          disabled={noMedicareCard}
          className="space-y-4 transition-opacity disabled:opacity-40 sm:space-y-6"
        >
          <QuizTextInput name="number" label="Medicare number" />
          <QuizTextInput name="expiry" label="Expiry MM/YYYY" />
        </fieldset>
        <div className="overflow-hidden rounded-xl bg-gray-100">
          <WhyRow onClick={onShowInformation} />
        </div>
        <NoMedicareCardCheckbox
          checked={noMedicareCard}
          // A valid Medicare card cannot also be missing.
          disabled={isValid || isSubmitting}
          onChange={onNoMedicareCardChange}
        />
        <BottomContinueButton
          initial={false}
          show={isValid || noMedicareCard}
          disabled={!isValid && !noMedicareCard}
          buttonProps={{
            type: 'submit',
            loading: isSubmitting,
          }}
        />
      </form>
    </FormProvider>
  )
}

function NoMedicareCardCheckbox({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) {
  let tileClass = 'bg-white ring-gray-300 hover:ring-gray-400'
  let boxClass = 'border-2 border-gray-400 bg-white'
  let labelClass = 'text-gray-900'
  if (checked) {
    tileClass = 'bg-white ring-gray-900'
    boxClass = 'bg-gray-900 text-white'
  } else if (disabled) {
    tileClass = 'cursor-not-allowed bg-gray-50 ring-gray-200'
    boxClass = 'border-2 border-gray-300 bg-gray-100'
    labelClass = 'text-gray-400'
  }

  return (
    <label
      data-testid="no-medicare-card"
      className={`flex h-16 cursor-pointer items-center gap-3.5 rounded-2xl px-5 ring-2 ring-inset transition has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-gray-900 ${tileClass}`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span
        aria-hidden
        className={`flex size-[22px] shrink-0 items-center justify-center rounded-md ${boxClass}`}
      >
        {checked && <FontAwesomeIcon icon={faCheck} className="size-3.5" />}
      </span>
      <span className={`text-[15px] font-medium leading-5 ${labelClass}`}>
        I don’t have a Medicare card
      </span>
    </label>
  )
}

type IhiStepProps = Props & { onShowInformation: () => void }

function IhiStep({
  answers,
  screenId,
  onChange,
  identifier,
  onShowInformation,
}: IhiStepProps) {
  const answer = answers[screenId]
  let defaultValues: object = {}
  if (
    answer &&
    answer.type === 'health-identifier' &&
    answer.identifier.type === 'ihi'
  ) {
    defaultValues = answer.identifier
  } else if (identifier && identifier.type === 'ihi') {
    defaultValues = identifier
  }

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onTouched',
    defaultValues: IHIFormValidator.cast(defaultValues),
    resolver: yupResolver(IHIFormValidator),
  })

  const { handleSubmit, formState } = methods
  const { isValid, isSubmitting } = formState
  const [deferring, setDeferring] = useState(false)

  async function onSubmit(values: IHIFormType) {
    await onChange(
      screenId,
      {
        id: screenId,
        type: 'health-identifier',
        identifier: {
          type: 'ihi',
          ihi: values.ihi,
        },
      },
      true,
    )
  }

  async function onDeferIdentifier() {
    setDeferring(true)
    try {
      // The Medicare answer stays empty; the patient adds it after payment.
      await onChange(
        HEALTH_IDENTIFIER_DEFERRED_KEY,
        { id: HEALTH_IDENTIFIER_DEFERRED_KEY, type: 'number', answer: 1 },
        true,
      )
    } finally {
      setDeferring(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-6 pb-6 sm:pb-11"
        onSubmit={handleSubmit(onSubmit)}
      >
        <QuizTextInput name="ihi" label="IHI number" />
        <div className="overflow-hidden rounded-xl bg-gray-100">
          <GetIhiDisclosure />
          <div aria-hidden className="mx-5 h-px bg-gray-200" />
          <WhyRow onClick={onShowInformation} />
        </div>
        <button
          type="button"
          disabled={isSubmitting || deferring}
          onClick={onDeferIdentifier}
          className="flex h-[52px] w-full items-center justify-center rounded-full border-[1.5px] border-gray-300 bg-white text-[15px] font-semibold leading-5 text-gray-900 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 disabled:cursor-wait disabled:opacity-60"
        >
          I’ll do this later
        </button>
        <BottomContinueButton
          initial={false}
          show={isValid}
          disabled={!isValid || deferring}
          buttonProps={{
            type: 'submit',
            loading: isSubmitting,
          }}
        />
      </form>
    </FormProvider>
  )
}

function GetIhiDisclosure() {
  const [open, setOpen] = useState(false)
  const contentId = useId()

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between px-5 py-4 text-left text-sm leading-5 text-gray-800 transition-colors hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-gray-900"
      >
        <span className={open ? 'font-semibold text-gray-900' : undefined}>
          How do I get an IHI number
        </span>
        <FontAwesomeIcon icon={open ? faMinus : faPlus} className="shrink-0" />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={contentId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ ease: 'easeOut', duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-4 px-5 pb-5 pt-3">
              <p className="text-sm leading-5 text-gray-600">{GET_IHI_INTRO}</p>
              <ol className="space-y-2.5">
                {GET_IHI_STEPS.map((text, index) => (
                  <li key={text} className="flex items-start gap-3">
                    <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-gray-900">
                      {index + 1}
                    </span>
                    <span className="pt-px text-sm leading-5 text-gray-800">
                      {text}
                    </span>
                  </li>
                ))}
              </ol>
              <a
                href={MYGOV_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-white pl-[18px] pr-4 text-sm font-semibold text-gray-900 sm:inline-flex sm:h-10 sm:w-auto transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
              >
                Open myGov
                <FontAwesomeIcon icon={faArrowUpRight} className="size-3.5" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
